import { useState } from "react";
import { normalizeLink } from "./links";
import { useNavigate } from "react-router";
import { Bell, BellOff, Check, ChevronDown, Fingerprint, LogOut, ShieldCheck, UserRound } from "lucide-react";
import { useMarkAllNotificationsRead, useMarkNotificationRead, useSession, useUnreadNotifications, useLiveEvent, useQueryClient, queryKeys, isApiError, type StepUpChallenge } from "@pmo/api-client";
import { enumLabel, pickLocalized, useFormat, useLocale, useTranslation } from "@pmo/i18n";
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  InlineAlert,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TenantBadge,
  cn,
  toast,
} from "@pmo/ui";

/** Workspace switcher: lists the user's memberships; switching posts to `/api/auth/switch-tenant` and refetches every query. */
export function TenantSwitcher() {
  const { t } = useTranslation("common");
  const { locale } = useLocale();
  const { tenant, memberships, switchTenant } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!tenant) return null;
  const name = (x: { nameEn: string; nameAr: string; shortNameEn?: string | null; shortNameAr?: string | null }, short = false) =>
    short ? pickLocalized(locale, x.shortNameEn || x.nameEn, x.shortNameAr || x.nameAr) : pickLocalized(locale, x.nameEn, x.nameAr);
  // With one workspace there is nothing to switch to; its name already sits under the logo.
  if (memberships.length <= 1) return null;
  const badge = <TenantBadge name={name(tenant, true)} kind={tenant.kind} accent={tenant.accent ?? undefined} className="max-w-56" />;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="hidden items-center gap-1 rounded-md p-0.5 pe-2 transition-colors hover:bg-aeblack-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring @4xl/topbar:inline-flex" aria-label={`${t("actions.switchTenant")}: ${name(tenant)}`} disabled={busy}>
          {badge}
          <ChevronDown className="size-3.5 text-subtle" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>{t("auth.workspaces")}</DropdownMenuLabel>
        {memberships.map((m) => {
          const current = m.tenant.id === tenant.id;
          return (
            <DropdownMenuItem
              key={m.tenant.id}
              disabled={current || busy}
              onSelect={async () => {
                setBusy(true);
                try {
                  await switchTenant(m.tenant.id);
                  toast.success(t("auth.switched", { name: name(m.tenant) }));
                  navigate("/");
                } finally {
                  setBusy(false);
                }
              }}
              className="flex items-start gap-2"
            >
              <span className="mt-1 size-2.5 shrink-0 rounded-full" style={{ background: m.tenant.accent ?? "var(--color-aegold-600)" }} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{name(m.tenant)}</span>
                <span className="block text-[11px] text-muted-foreground">
                  {enumLabel(locale, "tenantKind", m.tenant.kind)}
                  {m.isDefault ? ` · ${t("auth.defaultWorkspace")}` : ""}
                </span>
              </span>
              {current ? <Check className="size-4 text-aegold-700" aria-label={t("auth.currentWorkspace")} /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Bell with the unread count; the popover lists the latest unread notifications. Refreshes on `notification.created`. */
export function NotificationsBell() {
  const { t } = useTranslation("common");
  const { locale } = useLocale();
  const fmt = useFormat();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const unread = useUnreadNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  useLiveEvent("notification.created", () => void qc.invalidateQueries({ queryKey: queryKeys.notificationsRoot }));
  const count = unread.data?.total ?? 0;
  const items = unread.data?.items ?? [];
  const label = count ? t("notifications.unread", { count }) : t("notifications.open");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={label} className="relative text-aegold-600 hover:bg-white hover:text-aegold-700">
          <Bell aria-hidden />
          {count ? (
            <span className="absolute -end-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-danger-600 px-1 text-[10px] font-semibold text-white tabular-nums shadow-sm ring-2 ring-white" aria-hidden>
              {count > 99 ? "99+" : count}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
          <p className="text-sm font-semibold">{t("notifications.title")}</p>
          {count ? (
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
              {t("actions.markAllRead")}
            </Button>
          ) : null}
        </div>
        {unread.error ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">{isApiError(unread.error) && unread.error.isUnavailable ? t("errors.apiUnavailableTitle") : t("errors.genericTitle")}</p>
        ) : items.length ? (
          <ul className="max-h-96 divide-y divide-hairline overflow-y-auto p-1.5">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className="flex w-full flex-col gap-0.5 rounded-md px-3 py-2.5 text-start transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
                  onClick={() => {
                    markRead.mutate(n.id);
                    if (n.link) navigate(normalizeLink(n.link));
                  }}
                >
                  <span className="text-sm font-medium text-strong">{pickLocalized(locale, n.titleEn, n.titleAr)}</span>
                  {n.bodyEn || n.bodyAr ? <span className="line-clamp-2 text-xs text-subtle">{pickLocalized(locale, n.bodyEn, n.bodyAr)}</span> : null}
                  <span className="text-[11px] text-muted-foreground">{fmt.relative(n.createdAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col items-center gap-1 px-4 py-8 text-center">
            <BellOff className="size-5 text-sand-400" aria-hidden />
            <p className="text-sm font-medium">{t("notifications.empty")}</p>
            <p className="text-xs text-muted-foreground">{t("notifications.emptyHint")}</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function StepUpDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation("common");
  const { locale } = useLocale();
  const { startStepUp, completeStepUp } = useSession();
  const [challenge, setChallenge] = useState<StepUpChallenge | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reset = () => {
    setChallenge(null);
    setCode("");
    setError(null);
  };
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(isApiError(e) ? e.detail ?? e.title : t("errors.genericTitle"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t("auth.stepUp")}</DialogTitle>
          <DialogDescription>{challenge ? pickLocalized(locale, challenge.hintEn || challenge.hint, challenge.hintAr) : t("errors.stepUpRequired")}</DialogDescription>
        </DialogHeader>
        {challenge ? (
          <Field label={t("auth.stepUpCode")} error={error ?? undefined}>
            {(id) => <Input id={id} value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" dir="ltr" autoFocus />}
          </Field>
        ) : error ? (
          <InlineAlert tone="danger">{error}</InlineAlert>
        ) : null}
        <DialogFooter>
          {challenge ? (
            <Button
              primary
              loading={busy}
              disabled={!code.trim()}
              onClick={() =>
                run(async () => {
                  await completeStepUp(challenge.challengeId, code.trim());
                  onOpenChange(false);
                  reset();
                })
              }
            >
              {t("auth.stepUpVerify")}
            </Button>
          ) : (
            <Button primary loading={busy} onClick={() => run(async () => setChallenge(await startStepUp()))}>
              <Fingerprint aria-hidden />
              {t("auth.stepUpStart")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Avatar menu: name and title, roles in the current workspace, profile, identity (step-up) status, sign out. */
export function UserMenu({ profileHref = "/profile", loginPath = "/login" }: { profileHref?: string; loginPath?: string }) {
  const { t } = useTranslation("common");
  const { locale } = useLocale();
  const fmt = useFormat();
  const { user, session, logout } = useSession();
  const navigate = useNavigate();
  const [stepUp, setStepUp] = useState(false);
  if (!user) return null;
  const name = pickLocalized(locale, user.displayNameEn, user.displayNameAr);
  const title = pickLocalized(locale, user.titleEn, user.titleAr);
  const stepUpAt = session?.stepUpAt;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="rounded-md p-0.5 transition-colors duration-200 hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" aria-label={`${name}, ${t("auth.profile")}`} title={name}>
            <Avatar name={name} size="sm" className="size-9 text-xs" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel className="flex items-center gap-3 font-normal">
            <Avatar name={name} size="md" />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-strong">{name}</span>
              {title ? <span className="block truncate text-xs text-subtle">{title}</span> : null}
              <span className="block truncate text-xs text-muted-foreground" dir="ltr">{user.email}</span>
            </span>
          </DropdownMenuLabel>
          {session?.roles.length ? (
            <DropdownMenuLabel className="pt-0 text-[11px] font-normal">
              {t("auth.roles")}: {session.roles.map((r) => enumLabel(locale, "role", r)).join(locale === "ar" ? "، " : ", ")}
            </DropdownMenuLabel>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => navigate(profileHref)}>
            <UserRound aria-hidden />
            {t("auth.profile")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setStepUp(true)}>
            <ShieldCheck className={cn(stepUpAt ? "text-success-600" : "text-sand-500")} aria-hidden />
            <span className="flex flex-col">
              <span>{t("auth.stepUp")}</span>
              <span className="text-[11px] text-muted-foreground">{stepUpAt ? t("auth.stepUpRecent", { time: fmt.relative(stepUpAt) }) : t("auth.stepUpNone")}</span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            destructive
            onSelect={async () => {
              if (await logout()) return;
              toast.success(t("auth.signedOut"));
              navigate(loginPath, { replace: true });
            }}
          >
            <LogOut aria-hidden />
            {t("actions.signOut")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <StepUpDialog open={stepUp} onOpenChange={setStepUp} />
    </>
  );
}
